/**
 * Agent capacity + round-robin routing — persisted via human-agent `GET/PUT /api/v1/settings/routing`.
 * Assignable agents for transfers still use human-agent ops when inbox is live.
 */

import { isHumanAgentInboxLive } from "@/lib/inboxLiveConfig";
import { fetchAssignableAgents } from "@/api/humanAgentOpsApi";
import { workspaceHumanAgentSettingsClient } from "@/api/workspaceHumanAgentSettingsClient";
import { mapRoutingFromApi, mapRoutingToApi } from "@/api/workspaceRoutingSettingsMapper";
import { fetchRoutingAgentCandidates } from "@/api/workspaceRoutingAgentsApi";

/** @typedef {{ id: string, name: string, minConcurrentChats: number, maxConcurrentChats: number }} RoutingAgent */

/**
 * @typedef {{
 *   roundRobinEnabled: boolean,
 *   agentOrderIds: string[],
 *   roundRobinCursor: number,
 *   agents: RoutingAgent[],
 * }} AgentRoutingSettings */

/** @type {AgentRoutingSettings} */
export const DEFAULT_AGENT_ROUTING = {
  roundRobinEnabled: false,
  agentOrderIds: [],
  roundRobinCursor: 0,
  agents: [],
};

/**
 * @returns {Promise<{ data: AgentRoutingSettings }>}
 */
export async function getAgentRoutingSettings() {
  try {
    const [routing, dir] = await Promise.all([
      workspaceHumanAgentSettingsClient.getRouting(),
      fetchRoutingAgentCandidates().catch(() => ({ agents: [] })),
    ]);
    const mapped = mapRoutingFromApi(
      /** @type {Record<string, unknown>} */ (routing),
      /** @type {Array<{ id: number, name: string, email?: string }>} */ (dir.agents || [])
    );
    return { data: mapped };
  } catch {
    return { data: { ...DEFAULT_AGENT_ROUTING, agents: [], agentOrderIds: [] } };
  }
}

/**
 * @param {Partial<AgentRoutingSettings>} patch
 * @returns {Promise<{ data: AgentRoutingSettings }>}
 */
export async function saveAgentRoutingSettings(patch) {
  const prev = (await getAgentRoutingSettings()).data;
  const merged = {
    roundRobinEnabled: typeof patch.roundRobinEnabled === "boolean" ? patch.roundRobinEnabled : prev.roundRobinEnabled,
    agents: patch.agents ?? prev.agents,
    agentOrderIds: patch.agentOrderIds ?? prev.agentOrderIds,
    roundRobinCursor: typeof patch.roundRobinCursor === "number" ? patch.roundRobinCursor : prev.roundRobinCursor,
  };
  const body = mapRoutingToApi(merged);
  const saved = await workspaceHumanAgentSettingsClient.putRouting(body);
  const dir = await fetchRoutingAgentCandidates().catch(() => ({ agents: [] }));
  const data = mapRoutingFromApi(
    /** @type {Record<string, unknown>} */ (saved),
    /** @type {Array<{ id: number, name: string, email?: string }>} */ (dir.agents || [])
  );
  try {
    window.dispatchEvent(new CustomEvent("agent-routing-changed", { detail: data }));
  } catch {
    /* ignore */
  }
  return { data };
}

/**
 * Agents available to receive a transfer.
 *
 * @param {{ excludeAssignee?: string | null, excludeSessionId?: string | null, workspaceId?: string | null }} [params]
 * @returns {Promise<{ data: RoutingAgent[] }>}
 */
export async function listAssignableAgents(params = {}) {
  if (isHumanAgentInboxLive()) {
    const list = await fetchAssignableAgents({
      excludeSessionId: params.excludeSessionId ?? undefined,
      workspaceId: params.workspaceId ?? undefined,
    });
    return { data: list };
  }
  const { data } = await getAgentRoutingSettings();
  const ex = (params.excludeAssignee ?? "").trim();
  const list = data.agents.filter((a) => a.name !== ex && a.name !== "Unassigned");
  return { data: list };
}
